/**
 * Convert paise (smallest unit) to rupees with 2 decimal places.
 * Example: 1250000 → "12,500.00"
 *
 * Back-compat shim — delegates to the generic, subunit-aware {@link amountToMajor}
 * with the INR currency. Kept INR-named so existing call sites are untouched.
 *
 * @deprecated INR-hardcoded. Use {@link amountToMajor}`(paise, currency)` with
 * the tenant currency from `useTenantCurrency()`. Retained until the Axis-1
 * migration prompts retire the remaining INR-only call sites.
 */
export function paiseToRupees(paise: number): string {
    return amountToMajor(paise, 'INR');
}

/**
 * Convert paise to display string with ₹ symbol.
 * Example: 1250000 → "₹12,500.00"
 *
 * Back-compat shim. The default (no/`₹` symbol) path is byte-identical to the
 * legacy INR implementation and equals `formatAmount(paise, 'INR')`. Pass an
 * explicit `currency` (an ISO 4217 `CurrencyCode` from `@bbpos/types`, e.g.
 * `'OMR'`) to opt into the currency-aware path — symbol and decimals then come
 * from the currency, not the caller. Typed as `string` so `packages/utils`
 * stays dependency-free (the package is consumed via source paths and has no
 * project-reference wiring to `@bbpos/types`); any `CurrencyCode` is assignable.
 *
 * @deprecated The legacy symbol-arg form (`formatCurrency(paise)` /
 * `formatCurrency(paise, '₹')`) is INR-hardcoded. Frontends must call the
 * currency-aware app shim — `@/utils/currency` (admin) or `@/lib/format`
 * (pos) — passing the tenant currency from `useTenantCurrency()`. Equivalent
 * to {@link formatAmount}`(paise, currency)`. Retained until the Axis-1
 * migration prompts retire the legacy call sites.
 */
export function formatCurrency(paise: number, currencySymbol?: string): string;
export function formatCurrency(paise: number, currencySymbol: string, currency: string): string;
export function formatCurrency(
    paise: number,
    currencySymbol = '₹',
    currency?: string,
): string {
    if (currency !== undefined) {
        return formatAmount(paise, currency);
    }
    return `${currencySymbol}${paiseToRupees(paise)}`;
}

/**
 * Convert rupees to paise (integer).
 * Example: 125.50 → 12550
 *
 * Back-compat shim — delegates to the generic, subunit-aware {@link majorToAmount}
 * with the INR currency.
 */
export function rupeesToPaise(rupees: number): number {
    return majorToAmount(rupees, 'INR');
}

// ── Generic multi-currency helpers ──────────────────────────────────────────

/**
 * Number of subunits per major unit for each supported (in-scope) currency.
 * In-scope set = INR + the 6 GCC currencies (tenant currencies), plus the 14
 * further currencies a supplier may invoice in (PURCHASE_CURRENCIES in
 * @bbpos/types, docs/multi-currency-purchasing-plan.md D6). OMR/BHD/KWD are
 * 3-decimal currencies (factor 1000), JPY has none (factor 1); the rest are
 * 2-decimal (factor 100). Defaults to 100 for unknown codes (see getSubunitMultiplier).
 */
const CURRENCY_SUBUNITS: Record<string, number> = {
    INR: 100, AED: 100, SAR: 100, QAR: 100, OMR: 1000, BHD: 1000, KWD: 1000,
    USD: 100, EUR: 100, GBP: 100, SGD: 100, JPY: 1, CNY: 100, AUD: 100,
    CAD: 100, CHF: 100, THB: 100, MYR: 100, BDT: 100, LKR: 100, NPR: 100,
};

/**
 * Display symbols for the in-scope currencies. SAR/QAR/OMR conventionally share
 * the ambiguous rial sign (﷼), so they fall back to their ISO 4217 code — which
 * is also what formatAmount does for any code missing here. The purchase-only
 * currencies are deliberately absent: a bare `$` would not tell USD from SGD,
 * AUD or CAD on a supplier's bill, so they print as their code.
 */
const CURRENCY_SYMBOLS: Record<string, string> = {
    INR: '₹', AED: 'د.إ', SAR: 'SAR', QAR: 'QAR', OMR: 'OMR', BHD: '.د.ب', KWD: 'د.ك',
};

/**
 * Return the number of subunits per major unit for the given ISO 4217 currency code.
 * Defaults to 100 (standard 2-decimal currencies) for unknown codes.
 */
export function getSubunitMultiplier(currency: string): number {
    return CURRENCY_SUBUNITS[currency.toUpperCase()] ?? 100;
}

/**
 * Number of decimal places for a currency, derived from its subunit factor
 * (100 → 2, 1000 → 3, 1 → 0). Keeps display decimals consistent with the
 * minor-unit math instead of assuming 2.
 */
export function getCurrencyDecimals(currency: string): number {
    return Math.round(Math.log10(getSubunitMultiplier(currency)));
}

/**
 * Digit grouping, by currency.
 *
 * India groups in lakhs and crores — 1,25,050 — and that is right for INR and
 * wrong everywhere else. This used to be hardcoded to `en-IN`, so a Saudi shop
 * published `SAR 1,25,050.00` on its own storefront.
 *
 * Two locales, not seven, and deliberately so. `en-AE`, `en-SA` and the rest
 * produce output identical to `en-US` — 3-digit groups, Latin digits, a `.`
 * decimal — but they depend on the runtime carrying that locale's ICU data, and
 * a locale the runtime lacks falls back to the SYSTEM default, which on a
 * server is whatever the box was configured with. These two are present in
 * every JS runtime, so the answer is the same everywhere the code runs.
 */
const GROUPING_LOCALE: Readonly<Record<string, string>> = { INR: 'en-IN' };
const DEFAULT_GROUPING_LOCALE = 'en-US';

/**
 * Convert an amount in the smallest unit to a formatted major-unit string.
 * Example: amountToMajor(125050, 'INR') → "1,25,050.00" at lakh scale
 *          amountToMajor(125050, 'SAR') → "1,250.50"
 *          amountToMajor(12345, 'OMR')  → "12.345"
 */
export function amountToMajor(amount: number, currency: string): string {
    const multiplier = getSubunitMultiplier(currency);
    const major = amount / multiplier;
    const decimals = getCurrencyDecimals(currency);
    const locale = GROUPING_LOCALE[currency?.toUpperCase()] ?? DEFAULT_GROUPING_LOCALE;
    return major.toLocaleString(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
}

/**
 * Format an amount in the smallest unit with its currency symbol.
 * Example: formatAmount(125050, 'INR') → "₹1,250.50"
 *          formatAmount(500, 'USD')    → "$5.00"
 */
export function formatAmount(amount: number, currency: string): string {
    const symbol = CURRENCY_SYMBOLS[currency.toUpperCase()] ?? currency;
    return `${symbol}${amountToMajor(amount, currency)}`;
}

/**
 * Convert a major-unit value to the smallest integer unit.
 * Example: majorToAmount(12.50, 'INR') → 1250
 *          majorToAmount(1500, 'JPY')  → 1500
 */
export function majorToAmount(major: number, currency: string): number {
    return Math.round(major * getSubunitMultiplier(currency));
}

const RATE_SCALE = 10n ** 8n; // exchange rates are NUMERIC(18,8)
const RATE_PATTERN = /^(\d+)(?:\.(\d{1,8}))?$/;

/**
 * Convert an amount in `from`'s minor units to `to`'s minor units at `rate`
 * (units of `to` per 1 unit of `from`, in major units — the exchange_rates /
 * purchase_orders.exchange_rate convention). Exact: BigInt arithmetic on the
 * rate's 8 decimals, rounded half away from zero to `to`'s minor unit.
 * Same currency returns the amount unchanged.
 * Example: convertMinorUnits(100000, 'INR', 'OMR', '0.0046') → 4600 (INR 1,000 → OMR 4.600)
 */
export function convertMinorUnits(
    amount: number,
    from: string,
    to: string,
    rate: string | number,
): number {
    if (!Number.isSafeInteger(amount)) throw new RangeError(`amount must be an integer: ${amount}`);
    if (from.toUpperCase() === to.toUpperCase()) return amount;
    // pg returns NUMERIC as a float (database.module setTypeParser 1700); toFixed
    // recovers its 8 decimals without the exponent form String() gives below 1e-6.
    const text = typeof rate === 'number' ? rate.toFixed(8) : rate.trim();
    const match = RATE_PATTERN.exec(text);
    if (!match) throw new RangeError(`invalid exchange rate: ${rate}`);
    const scaledRate = BigInt(match[1]) * RATE_SCALE + BigInt((match[2] ?? '').padEnd(8, '0'));
    if (scaledRate === 0n) throw new RangeError('exchange rate must be greater than 0');

    const numerator = BigInt(Math.abs(amount)) * scaledRate * BigInt(getSubunitMultiplier(to));
    const denominator = RATE_SCALE * BigInt(getSubunitMultiplier(from));
    let result = numerator / denominator;
    if ((numerator % denominator) * 2n >= denominator) result += 1n;
    return amount < 0 ? -Number(result) : Number(result);
}

/**
 * Format paise as compact rupee string (no decimals) for dashboard KPIs.
 * Uses Math.floor per GST rounding rules.
 * Example: 1250099 → "₹12,500"
 *
 * Back-compat shim — INR output is preserved byte-identically. The divisor is
 * routed through {@link getSubunitMultiplier} (INR → 100) instead of a
 * hardcoded `/ 100` so the subunit factor stays single-sourced.
 *
 * @deprecated INR-hardcoded (symbol + `Math.floor` + `en-IN`). Use the
 * currency-aware app shim `formatPaiseCompact(paise, currency)` from
 * `@/utils/currency` with `useTenantCurrency()`. Retained until the Axis-1
 * migration prompts retire the legacy call sites.
 */
export function formatPaise(paise: number): string {
    const major = Math.floor(paise / getSubunitMultiplier('INR'));
    return `₹${major.toLocaleString('en-IN')}`;
}
